import { useRadio, type RadioProps as HeroUIRadioProps } from "@heroui/react";
import clsx from "clsx";
import { tv } from "tailwind-variants";

type Size = Extract<HeroUIRadioProps["size"], "sm" | "lg">;

export type RadioTileProps = Omit<HeroUIRadioProps, "size"> & {
  alignContent?: "left" | "center";
  isRequired?: boolean;
  size?: Size;
};

const radioTile = tv({
  base: `
    relative flex flex-1 justify-start cursor-pointer rounded-lg p-6 text-left 
    outline outline-[1px] outline-gray-600 
    hover:outline-2 hover:outline-black
  `,
  variants: {
    isInvalid: {
      true: `
        outline-danger 
        data-[selected=true]:outline-danger 
        data-[selected=true]:bg-danger-light
      `,
    },
    isRequired: {
      true: `
        outline-danger 
        data-[selected=true]:outline-danger 
        data-[selected=true]:bg-danger-light
      `,
    },
    isDisabled: {
      true: "pointer-events-none opacity-40",
    },
    isSelected: {
      true: "outline outline-2 outline-primary bg-primary-light hover:outline-primary",
    },
    isFocusVisible: {
      true: "z-10 ring-2 ring-focus ring-offset-4 ring-offset-background",
    },
    alignContent: {
      center: "items-center justify-center text-center",
      left: "items-start justify-start text-left",
    },
    size: {
      sm: "min-h-[92px] min-w-[183px]",
      lg: "min-h-[104px] min-w-[240px]",
    },
  },
});

const labelText = tv({
  base: "font-medium text-black",
  variants: {
    size: {
      sm: "text-medium",
      lg: "text-large",
    },
    isSelected: {
      true: "font-semibold",
    },
  },
  defaultVariants: {
    size: "sm",
    alignContent: "left",
  },
});

const descriptionText = tv({
  base: "text-small text-gray-600",
  variants: {
    isSelected: {
      true: "text-gray-900",
    },
  },
});

export const RadioTile = (props: RadioTileProps) => {
  const {
    children,
    Component,
    description,
    getBaseProps,
    getDescriptionProps,
    getInputProps,
    getLabelProps,
    getLabelWrapperProps,
    isDisabled,
    isFocusVisible,
    isInvalid,
    isSelected,
  } = useRadio(props);

  return (
    <Component
      {...getBaseProps()}
      className={radioTile({
        isInvalid,
        isDisabled,
        isSelected,
        isFocusVisible,
        isRequired: props.isRequired,
        alignContent: props.alignContent,
        size: props.size,
      })}
    >
      <input {...getInputProps()} tabIndex={0} />

      <div
        {...getLabelWrapperProps()}
        className={clsx("flex flex-col", {
          "gap-1": props.size === "sm",
          "gap-2": props.size === "lg",
        })}
      >
        {children && (
          <span
            {...getLabelProps()}
            className={labelText({
              size: props.size,
              isSelected,
            })}
          >
            {children}
          </span>
        )}
        {description && (
          <span
            {...getDescriptionProps()}
            className={descriptionText({ isSelected })}
          >
            {description}
          </span>
        )}
      </div>
    </Component>
  );
};
